import { useState } from 'react';
import { PlayCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useFeatureToggles } from '@/hooks/useFeatureToggles';
import { cn } from '@/lib/utils';
import { findTuto, type TutoId } from './tutorial-catalog';
import { TutorialPlayerDialog } from './TutorialPlayerDialog';

type TutorialButtonProps = {
  tutoId: TutoId;
  /** Icône seule (bandeaux chargés comme celui de la caisse). */
  compact?: boolean;
  className?: string;
};

/**
 * Bouton « Voir le tuto » : ouvre la vidéo d'une minute dans le lecteur
 * plein écran. Masqué si le flag `aide_videos` est désactivé pour le commerce.
 */
export function TutorialButton({ tutoId, compact = false, className }: TutorialButtonProps) {
  const { isFeatureEnabled } = useFeatureToggles();
  const [playing, setPlaying] = useState<TutoId | null>(null);
  if (!isFeatureEnabled('aide_videos')) return null;
  // Nom accessible générique : le titre du tuto (ex. « Clôture Z ») ne doit pas
  // se confondre avec les boutons d'action de l'écran (sélecteurs E2E par rôle).
  const tooltip = `Voir le tuto : ${findTuto(tutoId).title}`;

  return (
    <>
      <Button
        type="button"
        variant={compact ? 'ghost' : 'outline'}
        size={compact ? 'icon' : 'sm'}
        className={cn(compact ? 'h-8 w-8 text-primary' : 'gap-1.5 text-primary', className)}
        onClick={(e) => {
          e.stopPropagation();
          setPlaying(tutoId);
        }}
        title={tooltip}
        aria-label="Voir le tuto vidéo"
        data-testid={`tuto-btn-${tutoId}`}
      >
        <PlayCircle className="h-4 w-4" />
        {!compact && <span>Voir le tuto</span>}
      </Button>
      <TutorialPlayerDialog tutoId={playing} onClose={() => setPlaying(null)} />
    </>
  );
}
